import * as Yup from 'yup'

const inputTypes = {}

inputTypes.checkbox = {
  APIToFormValue: (modifiers) => {
    return modifiers.map(o => ({
      itemGuid: o.itemGuid,
      selected: o.selected
    }))
  },
  getSchema: ({ minSelections, maxSelections }) => {
    let valueSchema = Yup.array()
      .of(
        Yup.object().shape({
          selected: Yup.bool()
        })
      )
      .compact(a => !a.selected)
      .min(minSelections, 'min: ${min}') // eslint-disable-line no-template-curly-in-string

    if (maxSelections) {
      valueSchema = valueSchema.max(maxSelections, 'max: ${max}') // eslint-disable-line no-template-curly-in-string
    }

    const schema = Yup.object().shape({
      value: valueSchema
    })

    return schema
  },
  formValueToApi: (value, itemGroupGuid, quantity) =>
    value.reduce((acc, { itemGuid, selected }) => (
      !selected ? acc : [
        ...acc,
        {
          itemGuid,
          itemGroupGuid,
          quantity
        }
      ]
    ), [])
}

inputTypes.radio = {
  APIToFormValue: (modifiers) => {
    const found = modifiers.find(o => o.selected)
    return found ? found.itemGuid : ''
  },
  getSchema: () => Yup.object().shape({ value: Yup.string().required('required') }),
  formValueToApi: (itemGuid, itemGroupGuid, quantity) => (
    [
      {
        itemGuid,
        itemGroupGuid,
        quantity
      }
    ]
  )
}

inputTypes.fractionalQuantity = {
  defaultInitialValue: '1.00',
  getSchema: () => Yup.object().shape({
    fractionalQuantity: Yup.number().moreThan(0.24, 'Minimum is 0.25').required('Required')
  })
}

const isSingleSelect = (min, max) => min === 1 && max === 1

export const maxCharacters = 200

export const modifierFormHelpers = ({
  modifierGroups,
  itemGroupGuid,
  quantity = 1,
  usesFractionalQuantity = false,
  fractionalQuantity = {},
  specialInstructions = ''
}) => {
  const typeByGuid = {}
  const getType = guid => typeByGuid[guid]
  const initTypeByGuid = ({ guid, minSelections, maxSelections }) => {
    typeByGuid[guid] = isSingleSelect(minSelections, maxSelections) ? 'radio' : 'checkbox'
  }

  const getInitialModifiers = (modifierGroups) => {
    /** While also composing initialValues,
     * recursively loop through modifierGroups
     * to initModifierGroupByGuid */
    return modifierGroups.reduce((acc, group) => {
      initTypeByGuid(group)

      const { guid: groupGuid, modifiers } = group
      const { APIToFormValue } = inputTypes[getType(groupGuid)]

      const detailsByItemGuid = modifiers.reduce((ac, mod) => {
        const { itemGuid, modifierGroups = [], price, name } = mod
        return ({
          ...ac,
          [itemGuid]: {
            price,
            name,
            modifiers: getInitialModifiers(modifierGroups)
          }
        })
      }, {})

      return {
        ...acc,
        [groupGuid]: {
          detailsByItemGuid,
          value: APIToFormValue(modifiers)
        }
      }
    }, {})
  }

  // get initial values (deep)
  const initialValues = {
    modifiers: getInitialModifiers(modifierGroups),
    quantity,
    specialInstructions
  }

  // get schema shape (shallow)
  const schemaShape = modifierGroups.reduce((acc, group) => {
    const { guid: groupGuid } = group
    const { getSchema } = inputTypes[getType(groupGuid)]

    return { ...acc, [groupGuid]: getSchema(group) }
  }, {})

  // add special instructions to schema shape
  let schema = Yup.object().shape({
    modifiers: Yup.object().shape(schemaShape),
    specialInstructions: Yup.string().max(maxCharacters, 'max ${max} characters') // eslint-disable-line no-template-curly-in-string
  })

  // account for fractional quantity
  if (usesFractionalQuantity) {
    initialValues.fractionalQuantity = fractionalQuantity.quantity || inputTypes.fractionalQuantity.defaultInitialValue
    schema = schema.concat(inputTypes.fractionalQuantity.getSchema())
  }

  // calls corresponding formValueToApi function for radio/checkbox recursively
  const getModifierGroups = (values, quantity = 1) => Object.entries(values.modifiers).map(
    ([guid, { value, detailsByItemGuid }]) => {
      const { formValueToApi } = inputTypes[getType(guid)]
      const modifiers = formValueToApi(value, itemGroupGuid, quantity).map(({ itemGuid, ...rest }) => {
        const nestedValues = detailsByItemGuid[itemGuid]
        return {
          itemGuid,
          ...rest,
          // nestedValues will be undefined if a radio group's value is not selected (i.e. itemGuid === '')
          modifierGroups: nestedValues ? getModifierGroups(nestedValues, quantity) : []
        }
      })
      return { guid, modifiers }
    }
  )

  // get calcualted price from formik values recursively
  const getComputedPrice = values => {
    let res = 0
    getModifierGroups(values).forEach(({ guid: groupGuid, modifiers }) => {
      modifiers.forEach(({ itemGuid }) => {
        // itemGuid can be an empty string when radio is unselected
        if (itemGuid) {
          const details = values.modifiers[groupGuid].detailsByItemGuid[itemGuid]
          res += details.price + getComputedPrice(details)
        }
      })
    })
    return res
  }

  // get an array of names of selected guids from formik values shallowly
  const getSelectedNames = values => {
    const res = []
    getModifierGroups(values).forEach(({ guid: groupGuid, modifiers }) => {
      modifiers.forEach(({ itemGuid }) => {
        // itemGuid can be an empty string when radio is unselected
        if (itemGuid) {
          const details = values.modifiers[groupGuid].detailsByItemGuid[itemGuid]
          res.push(details.name)
        }
      })
    })
    return res
  }

  return {
    initialValues,
    schema,
    getModifierGroups,
    getComputedPrice,
    maxCharacters,
    getSelectedNames
  }
}
